/**
 * Spinner — the app's one loading indicator, built on Lucide's Loader2.
 * Anything that spins renders this component; the ratchet in
 * src/__tests__/asyncStatePatterns.test.ts rejects `animate-spin` elsewhere.
 *
 *   <Spinner />                                   // block, centred, padded
 *   <Spinner size="lg" message="Loading agents…" />
 *   <Spinner fullScreen />                        // centred, fills viewport
 *   <Spinner layout="inline" />                   // in a button / row / header
 *   <Spinner layout="inline" message="Loading…" /> // icon + text on one line
 *
 * `block` (the default) is a padded, centred area and paints the icon in the
 * brand colour. `inline` is a bare span: no padding, a small icon by default,
 * and it inherits the surrounding text colour so it reads correctly inside a
 * filled button.
 */

import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  message?: string;
  fullScreen?: boolean;
  /** `block` (default) centres in a padded area; `inline` sits in a line of content. */
  layout?: 'block' | 'inline';
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'size-4',
  md: 'size-6',
  lg: 'size-10',
};

export function Spinner({
  size,
  message,
  fullScreen = false,
  layout = 'block',
  className,
}: SpinnerProps) {
  if (layout === 'inline') {
    return (
      <span data-slot="spinner" className={cn('inline-flex items-center gap-2', className)}>
        <Loader2 aria-hidden className={cn('shrink-0 animate-spin', SIZE_CLASS[size ?? 'sm'])} />
        {message && <span className="text-sm">{message}</span>}
      </span>
    );
  }

  const inner = (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <Loader2 className={cn('animate-spin text-primary', SIZE_CLASS[size ?? 'md'])} />
      {message && <p className="text-sm text-muted-foreground">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div data-slot="spinner" className="flex min-h-screen items-center justify-center bg-background">
        {inner}
      </div>
    );
  }

  return (
    <div data-slot="spinner" className="flex items-center justify-center py-12">
      {inner}
    </div>
  );
}
